import type { Agorot } from './money';
import type { PayslipFields } from './payslip';
import type { UserAnswers } from './answers';
import type { Form106Fields } from './form106';

/**
 * `minimumWageData` is a data check: the payslip's printed minimum wage vs our parameters.
 * `payslipCompleteness`: mandatory payslip details the parser is sure are absent (Wage Protection Law s.24).
 * `missingPayslips`: gaps between the first and last loaded months of one employer (reported on the latest).
 * `cleaningMinimumWage`: contractors' cleaning workers, the sector minimum (general minimum + a supplement; unconfirmed, low).
 * `pensionerHealthTax`: a worker receiving the old-age pension pays health tax from the pension only (asked as a status).
 * `notice`: after a dismissal, notice days given vs the law's, and pay in lieu on the final payslip.
 * `leavingPay`: after "I left this job", on the final payslip: unused vacation paid out, recuperation for the final part-year.
 * `overtime`: overtime lines paid at their percent (125%/150%) of the hourly value; global overtime is info only.
 * `sick`: sick days — accrual (1.5 a full month, to 90), the balance's arithmetic and drops, sick pay per hour.
 * `vacation`: annual leave — accrual vs the legal minimum, the balance's arithmetic and drops, wage workers' day value.
 * `severance`: severance after leaving a job (asked: end date and reason), on the latest loaded payslip.
 * `travel`: the travel expenses line vs days worked (a missing line when days were worked).
 * `foreignDeposit`, `foreignDeductions`: the foreign-worker rule pack (deposit, deduction caps); hidden for anyone else.
 */
export const RULE_IDS = ['minimumWage', 'cleaningMinimumWage', 'pension', 'recuperation', 'minimumWageData', 'payslipCompleteness', 'missingPayslips', 'foreignDeposit', 'foreignDeductions', 'travel', 'severance', 'vacation', 'sick', 'overtime', 'leavingPay', 'notice', 'pensionerHealthTax'] as const;
export type RuleId = (typeof RULE_IDS)[number];
/** Checks on a yearly document (Form 106, src/form106), not on a payslip: kept out of RULE_IDS (the payslip rules). */
export const YEAR_CHECK_IDS = ['form106'] as const;
/** Every check a finding can come from. */
export type CheckId = RuleId | (typeof YEAR_CHECK_IDS)[number];
export const CHECK_IDS: readonly CheckId[] = [...RULE_IDS, ...YEAR_CHECK_IDS];

/**
 * - `verify`: something the user should check with their employer.
 * - `ok`: checked and nothing found (shown so the user sees what was covered).
 * - `cannot_check`: inputs missing or out of scope; `missing` says what.
 */
export type FindingStatus = 'verify' | 'ok' | 'cannot_check';
export type Severity = 'info' | 'low' | 'medium' | 'high';
export type Confidence = 'low' | 'medium' | 'high';

export interface Finding {
  ruleId: CheckId;
  status: FindingStatus;
  /** `info` for `ok` and `cannot_check`. */
  severity: Severity;
  confidence: Confidence;
  /**
   * i18n key for the text. Texts are phrased as "verify this", never as
   * accusations. Namespaced by rule, e.g. `pension.employerBelowMinimum`.
   */
  messageKey: `${CheckId}.${string}`;
  /** Values interpolated into the message (agorot are formatted by the UI). */
  messageParams?: Record<string, string | number>;
  /** Estimated amount the user may be owed, when it can be computed. */
  amount?: Agorot;
  /** Extra i18n notes shown with the finding, e.g. that the amount may be understated. */
  notes?: `${CheckId}.${string}`[];
  /**
   * Older than the time limit for claims for this check: kept for information, severity
   * lowered to low, and never counted in a total (use `claimableTotal`).
   */
  tooOldToClaim?: true;
  /**
   * Which part of a check with several (a Form 106's: `partYear`, `creditPoints`, `employers`, `otherIncome`,
   * `estimate`): the card is named for it (i18n `check.<ruleId>.<check>`) under one heading for the check.
   */
  check?: string;
  /** For `cannot_check`: the payslip fields or answers that were missing. */
  missing?: (keyof PayslipFields | keyof UserAnswers | keyof Form106Fields)[];
  /**
   * How the rule reached this finding, as data for the "More info" panel: the inputs it read, the
   * parameters it used (with their effective dates) and the arithmetic. Labels are i18n keys; the UI
   * formats the values. Every finding has at least one step.
   */
  steps?: Step[];
}

/** A value in a calculation step. Money is agorot; the UI formats everything. */
export type StepValue =
  | { money: Agorot }
  | { percent: number }
  | { count: number; unit?: 'hours' | 'days' | 'months' | 'years' }
  | { month: string }
  | { date: string }
  /** An i18n key, e.g. an answer such as `questions.payBasis.hourly`. */
  | { text: string }
  /** Names of payslip fields or answers (translated as `field.<name>`), e.g. what was missing. */
  | { fields: string[] }
  /** Pay months (YYYY-MM). */
  | { months: string[] };

/** The parameter a step used: which file, and the entry in force (its dates and whether it's confirmed). */
export interface StepParam {
  name: string;
  from: string;
  to: string | null;
  confirmed: boolean;
}

export interface Step {
  /** i18n key, `step.<...>`. */
  label: `step.${string}`;
  value?: StepValue;
  param?: StepParam;
}

/** Sum of amounts the user may still claim: `verify` findings with an amount, excluding ones too old to claim. */
export function claimableTotal(findings: readonly Finding[]): Agorot {
  return findings.reduce((t, f) => (f.status === 'verify' && !f.tooOldToClaim && f.amount ? t + f.amount : t), 0) as Agorot;
}
